import React, { useState, useRef } from 'react';
import {
    Box, Typography, TextField, Button, MenuItem, Grid, Paper, Select, InputLabel, FormControl,
    useTheme, useMediaQuery, Fade, Zoom, Container, Stack, Alert, CircularProgress, Snackbar, Divider, Chip
} from '@mui/material';
import FunctionsIcon from '@mui/icons-material/Functions';
import GradeIcon from '@mui/icons-material/Grade';
import SubjectIcon from '@mui/icons-material/School';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import SpeedIcon from '@mui/icons-material/Speed';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DownloadIcon from '@mui/icons-material/Download';
import GenerateIcon from '@mui/icons-material/AutoFixHigh';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentIcon from '@mui/icons-material/Assignment';
//import axios from 'axios';
import api from '../../axios/axiosInstance';

import html2pdf from 'html2pdf.js';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from "docx";
import { SpinningGradelyLoader, FloatingGradelyLoader } from '../../loadingIcons/LoadingIcons';


const USE_MOCK = true; // ✅ Toggle this to false to switch back to backend

const GRADES = ['KG', 1, 2, 3, 4, 5, 6, 7, 8];
const SUBJECTS = ['Math'];

const MathWorksheetGenerator = () => {
    const [gradeLevel, setGradeLevel] = useState('');
    const [subject, setSubject] = useState('');
    const [topic, setTopic] = useState('');
    const [questionsPerDOK, setQuestionsPerDOK] = useState({ 1: '', 2: '', 3: '', 4: '' });
    const [worksheet, setWorksheet] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const worksheetRef = useRef();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleChange = (level, value) => {
        setQuestionsPerDOK((prev) => ({ ...prev, [level]: value }));
    };

    const handleGenerate = async () => {
        if (!gradeLevel || !topic) {
            setError('Please fill in grade level and topic.');
            return;
        }
        
        // Check if at least one DOK level has a value
        const hasAtLeastOneDOK = Object.values(questionsPerDOK).some(v => v !== '' && v !== null && v !== undefined);
        if (!hasAtLeastOneDOK) {
            setError('Please specify questions for at least one DOK level.');
            return;
        }

        setLoading(true);
        setWorksheet(null);
        setError('');

        try {


            const res = await api.post(`${process.env.REACT_APP_BACKEND_URL}/grades/generate-math-worksheet/`, {
                grade_level: String(gradeLevel),
                topic,
                questions_per_dok: questionsPerDOK
            }, { withCredentials: true });
            setWorksheet(res.data);

        } catch (err) {
            console.error('❌ API Error:', err);
            setError('Failed to generate worksheet.');
        } finally {
            setLoading(false);
        }
    };

    const generatePDFHTML = (worksheetData) => {
        // Theme colors for PDF
        const primaryColor = '#546A2F'; // Theme main color
        const secondaryColor = '#A8B87C'; // Theme secondary color
        const successColor = '#2e7d32'; // Material-UI success green
        const infoColor = '#0288d1'; // Material-UI info blue
        const warningColor = '#ed6c02'; // Material-UI warning orange
        const errorColor = '#d32f2f'; // Material-UI error red
        const greyColor = '#757575'; // Material-UI grey
        const lightGreyColor = '#f5f5f5'; // Light grey background
        const borderColor = '#e0e0e0'; // Light border color

        const html = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Math Worksheet - Grade ${gradeLevel}</title>
            </head>
            <body style="font-family: 'Times New Roman', serif; margin: 0; padding: 0; line-height: 1.5; color: #212121; font-size: 11pt; background: white;">
                <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid ${primaryColor}; padding-bottom: 15px;">
                    <div style="font-size: 20pt; font-weight: bold; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px; color: ${primaryColor};">Math Worksheet</div>
                    <div style="font-size: 12pt; font-weight: bold; margin-bottom: 5px; color: ${secondaryColor};">Grade ${gradeLevel} | ${topic}</div>
                </div>

                <div style="background: ${lightGreyColor}; border: 2px solid ${primaryColor}; padding: 15px; margin-bottom: 25px; border-radius: 8px;">
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
                        <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid ${borderColor};">
                            <span style="font-weight: bold; color: ${primaryColor}; font-size: 10pt; text-transform: uppercase;">Subject:</span>
                            <span style="font-weight: bold; color: #212121; font-size: 10pt;">${subject}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid ${borderColor};">
                            <span style="font-weight: bold; color: ${primaryColor}; font-size: 10pt; text-transform: uppercase;">Grade:</span>
                            <span style="font-weight: bold; color: ${successColor}; font-size: 10pt;">${gradeLevel}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid ${borderColor};">
                            <span style="font-weight: bold; color: ${primaryColor}; font-size: 10pt; text-transform: uppercase;">Topic:</span>
                            <span style="font-weight: bold; color: ${secondaryColor}; font-size: 10pt;">${topic}</span>
                        </div>
                    </div>
                </div>

                <div style="background: ${lightGreyColor}; border: 2px solid ${borderColor}; padding: 15px; margin-bottom: 20px; border-radius: 8px;">
                    <div style="display: inline-block; margin-right: 30px; margin-bottom: 8px;">
                        <strong style="font-weight: bold; color: ${primaryColor}; font-size: 10pt;">Name:</strong><span style="padding: 0 15px; margin-left: 8px;">________________________</span>
                    </div>
                    <div style="display: inline-block; margin-right: 30px; margin-bottom: 8px;">
                        <strong style="font-weight: bold; color: ${primaryColor}; font-size: 10pt;">Date:</strong><span style="padding: 0 15px; margin-left: 8px;">_______________</span>
                    </div>
                    <div style="display: inline-block; margin-right: 30px; margin-bottom: 8px;">
                        <strong style="font-weight: bold; color: ${primaryColor}; font-size: 10pt;">Class:</strong><span style="padding: 0 15px; margin-left: 8px; text-decoration: underline; color: ${secondaryColor};">Grade ${gradeLevel}</span>
                    </div>
                </div>

                <div style="background: ${lightGreyColor}; border: 2px solid ${warningColor}; padding: 12px; margin-bottom: 20px; border-radius: 8px; font-size: 10pt; font-weight: bold; color: ${warningColor};">
                    Instructions: Solve the following questions. Show your work clearly. Write your answers in the spaces provided.
                </div>

                ${Object.entries(worksheetData).map(([key, value]) => `
                    <div style="margin-bottom: 25px; page-break-inside: avoid;">
                        <div style="font-size: 14pt; font-weight: bold; color: ${primaryColor}; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.5px;">${key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                        <div style="background: white; border: 2px solid ${borderColor}; padding: 15px; margin-bottom: 15px; border-radius: 8px;">
                            ${typeof value === 'string' ?
                `<div style="font-size: 10pt; line-height: 1.4; text-align: justify; margin-bottom: 10px; color: #212121;">${value}</div>` :
                Array.isArray(value) ?
                    (key.toLowerCase().includes('question') ?
                        `<div>${value.map((item, index) => `<div style="margin-bottom: 12px; padding-left: 25px; position: relative; text-align: left; font-size: 11pt; font-weight: bold;"><span style="position: absolute; left: 0; font-weight: bold; color: ${primaryColor}; font-size: 11pt;">${index + 1}. </span><span style="color: #212121;">${item}</span></div>`).join('')}</div>` :
                        value.map(item => `<div style="margin-bottom: 8px; padding-left: 20px; position: relative; text-align: justify; font-size: 10pt;"><span style="position: absolute; left: 0; font-weight: bold; color: ${primaryColor}; font-size: 12pt; top: -1px;">•</span><span style="color: #212121;">${item}</span></div>`).join('')
                    ) :
                    typeof value === 'object' && value !== null ?
                        Object.entries(value).map(([subKey, subValue]) => `
                                    <div style="margin-bottom: 15px;">
                                        <div style="font-weight: bold; color: ${secondaryColor}; margin-bottom: 6px; font-size: 10pt; text-transform: uppercase;">${subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</div>
                                        ${Array.isArray(subValue) ?
                                (subKey.toLowerCase().includes('question') ?
                                    `<div>${subValue.map((item, index) => `<div style="margin-bottom: 12px; padding-left: 25px; position: relative; text-align: left; font-size: 11pt; font-weight: bold;"><span style="position: absolute; left: 0; font-weight: bold; color: ${primaryColor}; font-size: 11pt;">${index + 1}. </span><span style="color: #212121;">${item}</span></div>`).join('')}</div>` :
                                    subValue.map(item => `<div style="margin-bottom: 8px; padding-left: 20px; position: relative; text-align: justify; font-size: 10pt;"><span style="position: absolute; left: 0; font-weight: bold; color: ${primaryColor}; font-size: 12pt; top: -1px;">•</span><span style="color: #212121;">${item}</span></div>`).join('')
                                ) :
                                `<div style="font-size: 10pt; line-height: 1.4; text-align: justify; margin-bottom: 10px; color: #212121;">${subValue}</div>`
                            }
                                    </div>
                                `).join('') :
                        `<div style="font-size: 10pt; line-height: 1.4; text-align: justify; margin-bottom: 10px; color: #212121;">${String(value)}</div>`
            }
                        </div>
                    </div>
                `).join('')}
            </body>
            </html>
        `;
        return html;
    };

    const handleDownloadPDF = () => {
        if (!worksheet) return;

        const pdfHTML = generatePDFHTML(worksheet);
        const blob = new Blob([pdfHTML], { type: 'text/html' });
        const url = URL.createObjectURL(blob);

        const opt = {
            margin: [0.5, 0.5, 0.5, 0.5],
            filename: `Math Worksheet Grade - ${gradeLevel}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                scrollX: 0,
                scrollY: 0
            },
            jsPDF: {
                unit: 'in',
                format: 'a4',
                orientation: 'portrait',
                compress: true
            },
            pagebreak: { mode: 'avoid-all' }
        };

        // Create a temporary iframe to render the HTML
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.left = '-9999px';
        iframe.style.top = '-9999px';
        iframe.style.width = '800px';
        iframe.style.height = '600px';
        document.body.appendChild(iframe);

        iframe.onload = () => {
            setTimeout(() => {
                html2pdf().set(opt).from(iframe.contentDocument.body).save().then(() => {
                    document.body.removeChild(iframe);
                    URL.revokeObjectURL(url);
                });
            }, 500);
        };

        iframe.src = url;
    };

    const generateWordDocument = async (worksheetData) => {
        // Theme colors for Word document (matching PDF colors)
        const primaryColor = '546A2F'; // Theme main color
        const secondaryColor = 'A8B87C'; // Theme secondary color
        const successColor = '2e7d32'; // Material-UI success green
        const infoColor = '0288d1'; // Material-UI info blue
        const warningColor = 'ed6c02'; // Material-UI warning orange
        const errorColor = 'd32f2f'; // Material-UI error red

        const doc = new Document({
            sections: [
                {
                    properties: {},
                    children: [
                        // Title
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "MATH WORKSHEET",
                                    bold: true,
                                    size: 40,
                                    color: primaryColor
                                })
                            ],
                            heading: HeadingLevel.TITLE,
                            alignment: AlignmentType.CENTER,
                            spacing: { after: 300 }
                        }),

                        // Grade and Topic Information
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: `Grade ${gradeLevel} | ${topic}`,
                                    bold: true,
                                    size: 24,
                                    color: secondaryColor
                                })
                            ],
                            alignment: AlignmentType.CENTER,
                            spacing: { after: 400 }
                        }),

                        // Worksheet Information Section
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "WORKSHEET INFORMATION",
                                    bold: true,
                                    size: 28,
                                    color: primaryColor
                                })
                            ],
                            heading: HeadingLevel.HEADING_1,
                            spacing: { before: 300, after: 200 }
                        }),

                        // Grade
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "Grade: ",
                                    bold: true,
                                    size: 22,
                                    color: primaryColor
                                }),
                                new TextRun({
                                    text: gradeLevel,
                                    size: 22
                                })
                            ],
                            spacing: { after: 100 }
                        }),

                        // Subject
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "Subject: ",
                                    bold: true,
                                    size: 22,
                                    color: primaryColor
                                }),
                                new TextRun({
                                    text: subject,
                                    size: 22
                                })
                            ],
                            spacing: { after: 100 }
                        }),

                        // Topic
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "Topic: ",
                                    bold: true,
                                    size: 22,
                                    color: primaryColor
                                }),
                                new TextRun({
                                    text: topic,
                                    size: 22
                                })
                            ],
                            spacing: { after: 100 }
                        }),

                        // Questions per DOK Level
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "Questions per DOK Level: ",
                                    bold: true,
                                    size: 22,
                                    color: primaryColor
                                }),
                                new TextRun({
                                    text: Object.entries(questionsPerDOK).map(([level, count]) => `DOK ${level}: ${count}`).join(', '),
                                    size: 22,
                                    color: successColor
                                })
                            ],
                            spacing: { after: 400 }
                        }),

                        // Worksheet Content Sections
                        ...Object.entries(worksheetData).flatMap(([key, value]) => [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                                        bold: true,
                                        size: 28,
                                        color: primaryColor
                                    })
                                ],
                                heading: HeadingLevel.HEADING_1,
                                spacing: { before: 300, after: 200 }
                            }),

                            // Handle different value types
                            ...(typeof value === 'string' ? [
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: value,
                                            size: 22
                                        })
                                    ],
                                    spacing: { after: 300 }
                                })
                            ] : Array.isArray(value) ?
                                value.map((item, index) => {
                                    if (typeof item === 'string') {
                                        return new Paragraph({
                                            children: [
                                                new TextRun({
                                                    text: `${index + 1}. `,
                                                    bold: true,
                                                    size: 22,
                                                    color: primaryColor
                                                }),
                                                new TextRun({
                                                    text: item,
                                                    size: 22
                                                })
                                            ],
                                            spacing: { after: 100 }
                                        });
                                    } else if (typeof item === 'object' && item !== null) {
                                        // Handle question objects with DOK levels
                                        return Object.entries(item).flatMap(([subKey, subValue]) => [
                                            new Paragraph({
                                                children: [
                                                    new TextRun({
                                                        text: subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) + ":",
                                                        bold: true,
                                                        size: 24,
                                                        color: secondaryColor
                                                    })
                                                ],
                                                spacing: { before: 200, after: 100 }
                                            }),
                                            ...(Array.isArray(subValue) ?
                                                subValue.map((subItem, subIndex) =>
                                                    new Paragraph({
                                                        children: [
                                                            new TextRun({
                                                                text: `${subIndex + 1}. `,
                                                                bold: true,
                                                                size: 22,
                                                                color: primaryColor
                                                            }),
                                                            new TextRun({
                                                                text: subItem,
                                                                size: 22
                                                            })
                                                        ],
                                                        spacing: { after: 50 }
                                                    })
                                                ) : [
                                                    new Paragraph({
                                                        children: [
                                                            new TextRun({
                                                                text: String(subValue),
                                                                size: 22
                                                            })
                                                        ],
                                                        spacing: { after: 100 }
                                                    })
                                                ]
                                            )
                                        ]).flat();
                                    }
                                    return new Paragraph({
                                        children: [
                                            new TextRun({
                                                text: String(item),
                                                size: 22
                                            })
                                        ],
                                        spacing: { after: 100 }
                                    });
                                }).flat() :
                                typeof value === 'object' && value !== null ?
                                    Object.entries(value).flatMap(([subKey, subValue]) => [
                                        new Paragraph({
                                            children: [
                                                new TextRun({
                                                    text: subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) + ":",
                                                    bold: true,
                                                    size: 24,
                                                    color: secondaryColor
                                                })
                                            ],
                                            spacing: { before: 200, after: 100 }
                                        }),
                                        ...(Array.isArray(subValue) ?
                                            subValue.map((item, index) =>
                                                new Paragraph({
                                                    children: [
                                                        new TextRun({
                                                            text: `${index + 1}. `,
                                                            bold: true,
                                                            size: 22,
                                                            color: primaryColor
                                                        }),
                                                        new TextRun({
                                                            text: item,
                                                            size: 22
                                                        })
                                                    ],
                                                    spacing: { after: 100 }
                                                })
                                            ) : [
                                                new Paragraph({
                                                    children: [
                                                        new TextRun({
                                                            text: String(subValue),
                                                            size: 22
                                                        })
                                                    ],
                                                    spacing: { after: 100 }
                                                })
                                            ]
                                        )
                                    ]) : [
                                        new Paragraph({
                                            children: [
                                                new TextRun({
                                                    text: String(value),
                                                    size: 22
                                                })
                                            ],
                                            spacing: { after: 300 }
                                        })
                                    ]
                            )
                        ]),

                        // Footer
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "Generated by Gradely Educational Platform",
                                    size: 18,
                                    italics: true,
                                    color: "757575"
                                })
                            ],
                            alignment: AlignmentType.CENTER,
                            spacing: { before: 600 }
                        })
                    ]
                }
            ]
        });

        return doc;
    };

    const downloadWordDocument = async (worksheetData) => {
        try {
            const doc = await generateWordDocument(worksheetData);
            const blob = await Packer.toBlob(doc);

            const element = document.createElement('a');
            element.href = URL.createObjectURL(blob);
            element.download = `Math_Worksheet_Grade${gradeLevel}_${topic.replace(/\s+/g, '_')}.docx`;
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
            URL.revokeObjectURL(element.href);
        } catch (error) {
            console.error('Error generating Word document:', error);
            alert('Error generating Word document. Please try again.');
        }
    };

    const renderWorksheet = (worksheetData) => {
        if (!worksheetData) return null;

        return (
            <Fade in timeout={600}>
                <Box ref={worksheetRef}>
                    <Typography
                        variant={isSmallMobile ? "h6" : "h5"}
                        fontWeight="bold"
                        gutterBottom
                        sx={{
                            textAlign: 'center',
                            color: theme.palette.primary.main,
                            mb: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2
                        }}
                    >
                        <MenuBookIcon sx={{ fontSize: 'inherit' }} />
                        Generated Math Worksheet
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    {/* Basic Information */}
                    <Paper
                        elevation={2}
                        sx={{
                            p: { xs: 2, sm: 3 },
                            mb: 4,
                            borderRadius: { xs: 1, sm: 1.5, md: 2 },
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.paper
                        }}
                    >
                        <Typography
                            variant="h6"
                            fontWeight="bold"
                            gutterBottom
                            sx={{
                                color: theme.palette.primary.main,
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <AssignmentIcon fontSize="small" />
                            Worksheet Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <GradeIcon fontSize="small" color="primary" />
                                    <strong>Grade:</strong> {gradeLevel}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SubjectIcon fontSize="small" color="primary" />
                                    <strong>Subject:</strong> {subject}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <EmojiObjectsIcon fontSize="small" color="primary" />
                                    <strong>Topic:</strong> {topic}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <HelpOutlineIcon fontSize="small" color="primary" />
                                    <strong>Questions:</strong> {Object.values(questionsPerDOK).reduce((sum, val) => sum + parseInt(val, 10), 0)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Worksheet Content */}
                    {Object.entries(worksheetData).map(([key, value], index) => (
                        <Zoom in timeout={600 + index * 100} key={key}>
                            <Paper
                                elevation={2}
                                sx={{
                                    p: { xs: 2, sm: 3 },
                                    mb: 3,
                                    borderRadius: { xs: 1, sm: 1.5, md: 2 },
                                    border: `1px solid ${theme.palette.divider}`,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        boxShadow: 4,
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    sx={{
                                        textTransform: 'capitalize',
                                        mb: 2,
                                        color: theme.palette.primary.main,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}
                                >
                                    {key.replace(/_/g, ' ')}
                                </Typography>

                                {typeof value === 'string' && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            whiteSpace: 'pre-wrap',
                                            lineHeight: 1.6,
                                            fontSize: isSmallMobile ? '0.875rem' : '1rem'
                                        }}
                                    >
                                        {value}
                                    </Typography>
                                )}

                                {Array.isArray(value) && (
                                    <Box component="ol" sx={{ pl: 2, m: 0 }}>
                                        {value.map((item, index) => (
                                            <Box component="li" key={index} sx={{ mb: 2 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: isSmallMobile ? '0.875rem' : '1rem',
                                                        lineHeight: 1.6
                                                    }}
                                                >
                                                    {item}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {typeof value === 'object' && !Array.isArray(value) && value !== null && (
                                    <Box sx={{ pl: 2 }}>
                                        {Object.entries(value).map(([subKey, subValue]) => (
                                            <Box key={subKey} sx={{ mb: 2 }}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="medium"
                                                    sx={{
                                                        color: theme.palette.secondary.main,
                                                        mb: 1
                                                    }}
                                                >
                                                    {subKey.replace(/_/g, ' ')}:
                                                </Typography>
                                                {Array.isArray(subValue) ? (
                                                    <Box component="ol" sx={{ pl: 2, m: 0 }}>
                                                        {subValue.map((item, idx) => (
                                                            <Box component="li" key={idx} sx={{ mb: 1 }}>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontSize: isSmallMobile ? '0.875rem' : '1rem',
                                                                        lineHeight: 1.6
                                                                    }}
                                                                >
                                                                    {item}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            whiteSpace: 'pre-wrap',
                                                            lineHeight: 1.6,
                                                            fontSize: isSmallMobile ? '0.875rem' : '1rem'
                                                        }}
                                                    >
                                                        {subValue}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Paper>
                        </Zoom>
                    ))}
                </Box>
            </Fade>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 } }}>
            <Fade in timeout={800}>
                <Box>
                    <Box sx={{ textAlign: 'center', mb: 2, mt: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 0.5 }}>
                            <FunctionsIcon sx={{ fontSize: '2rem', color: theme.palette.primary.main }} />
                            <Typography sx={{ fontSize: '2rem', fontWeight: 600, color: theme.palette.primary.main }}>
                                Math Worksheet Generator
                            </Typography>
                        </Box>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    <Paper
                        elevation={3}
                        sx={{
                            p: { xs: 2, sm: 3, md: 4 },
                            borderRadius: { xs: 1.5, sm: 2, md: 3 },
                            background: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`
                        }}
                    >
                        <Stack spacing={3}>
                            <FormControl fullWidth>
                                <InputLabel>Grade Level</InputLabel>
                                <Select
                                    value={gradeLevel}
                                    label="Grade Level"
                                    onChange={(e) => setGradeLevel(e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: { xs: 1, sm: 1.5, md: 2 }
                                        }
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <GradeIcon fontSize="small" />
                                            Select a grade level
                                        </Box>
                                    </MenuItem>
                                    {GRADES.map((g) => (
                                        <MenuItem key={g} value={g}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <GradeIcon fontSize="small" />
                                                Grade {g}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel>Subject</InputLabel>
                                <Select
                                    value={subject}
                                    label="Subject"
                                    onChange={(e) => setSubject(e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: { xs: 1, sm: 1.5, md: 2 }
                                        }
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SubjectIcon fontSize="small" />
                                            Select a subject
                                        </Box>
                                    </MenuItem>
                                    {SUBJECTS.map((sub) => (
                                        <MenuItem key={sub} value={sub}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <SubjectIcon fontSize="small" />
                                                {sub}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                label="Topic"
                                fullWidth
                                value={topic}
                                onChange={(e) => setTopic(e.target.value)}
                                placeholder="e.g., Fractions, Addition, Geometry"
                                InputProps={{
                                    startAdornment: <EmojiObjectsIcon sx={{ mr: 1, color: 'action.active' }} />
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: { xs: 1, sm: 1.5, md: 2 }
                                    }
                                }}
                            />

                            <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                sx={{
                                    mb: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <HelpOutlineIcon fontSize="small" />
                                Questions per DOK Level:
                            </Typography>

                            <Grid container spacing={{ xs: 1, sm: 2 }}>
                                {[1, 2, 3, 4].map((level) => (
                                    <Grid item xs={12} sm={6} key={level}>
                                        <TextField
                                            label={`DOK Level ${level}`}
                                            type="number"
                                            fullWidth
                                            value={questionsPerDOK[level]}
                                            onChange={(e) => handleChange(level, e.target.value)}
                                            InputProps={{
                                                startAdornment: <SpeedIcon sx={{ mr: 1, color: 'action.active' }} />
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: { xs: 1, sm: 1.5, md: 2 }
                                                }
                                            }}
                                        />
                                    </Grid>
                                ))}
                            </Grid>

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleGenerate}
                                disabled={loading}
                                size="large"
                                sx={{
                                    py: 1.5,
                                    borderRadius: { xs: 1, sm: 1.5, md: 2 },
                                    fontSize: '1.1rem',
                                    fontWeight: 'bold',
                                    backgroundColor: theme.palette.primary.main,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                        transform: 'translateY(-2px)',
                                        boxShadow: 4
                                    }
                                }}
                            >
                                {loading ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CircularProgress size={20} color="inherit" />
                                        <span>Generating...</span>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <GenerateIcon />
                                        <span>Generate Worksheet</span>
                                    </Stack>
                                )}
                            </Button>
                        </Stack>
                    </Paper>

                    {worksheet && (
                        <Box sx={{ mt: 4 }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                mb: 3,
                                flexDirection: isSmallMobile ? 'column' : 'row',
                                gap: 2
                            }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleDownloadPDF}
                                    sx={{
                                        borderRadius: { xs: 1, sm: 1.5, md: 2 },
                                        backgroundColor: theme.palette.primary.main,
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.dark,
                                            transform: 'translateY(-2px)',
                                            boxShadow: 4
                                        }
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <DownloadIcon />
                                        <span>Download PDF</span>
                                    </Stack>
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => downloadWordDocument(worksheet)}
                                    sx={{
                                        borderRadius: 2,
                                        backgroundColor: theme.palette.primary.main,
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.dark,
                                            transform: 'translateY(-2px)',
                                            boxShadow: 4
                                        }
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <DownloadIcon />
                                        <span>Download Word</span>
                                    </Stack>
                                </Button>
                            </Box>

                            <Paper
                                elevation={4}
                                sx={{
                                    p: { xs: 2, sm: 3, md: 4 },
                                    borderRadius: { xs: 1.5, sm: 2, md: 3 },
                                    // maxHeight: 600, 
                                    overflowY: 'auto',
                                    background: theme.palette.background.default,
                                    border: `1px solid ${theme.palette.divider}`,
                                    '@media print': {
                                        borderRadius: 0,
                                        boxShadow: 'none',
                                        margin: 0,
                                        padding: '20px',
                                        maxHeight: 'none',
                                        overflowY: 'visible',
                                        pageBreakInside: 'avoid',
                                        breakInside: 'avoid'
                                    }
                                }}
                            >
                                {renderWorksheet(worksheet)}
                            </Paper>
                        </Box>
                    )}

                    <Snackbar
                        open={!!error}
                        autoHideDuration={6000}
                        onClose={() => setError('')}
                    >
                        <Alert severity="error" onClose={() => setError('')}>
                            {error}
                        </Alert>
                    </Snackbar>
                </Box>
            </Fade>

            {/* Loading State */}
            {loading && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        backdropFilter: "blur(8px)",
                        zIndex: 2000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            p: { xs: 3, sm: 4 },
                            borderRadius: 4,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            backgroundColor: "white",
                            maxWidth: 400,
                            mx: 2
                        }}
                    >
                        <FloatingGradelyLoader
                            size={60}
                            showText={true}
                            text="Generating custom math worksheet with practice problems..."
                        />
                    </Paper>
                </Box>
            )}
        </Container>
    );
};

export default MathWorksheetGenerator;